import { CardShell } from '@atlas/ui-components'
import { Badge, cn } from '@atlas/ui-primitives'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { useRef } from 'react'
import { WorkOrderCard } from '@/components/work-orders/WorkOrderCard'
import type { BoardWorkflow, WorkOrder } from '@/domain/work-order.types'
import type { BoardColumn, BoardModel, BoardWorkflowGroup } from './board-model'

/**
 * A drop target's id encodes the workflow as well as the status, because two
 * workflows may define the same status code: `'<workflowId>|backlog'`. Parsed
 * back in the board's onDragEnd, which uses the workflow to reject a
 * cross-workflow drop.
 */
export const dropId = (workflowId: string, status: string) => `${workflowId}|${status}`

export const parseDropId = (id: string): { workflowId: string; status: string } => {
	const [workflowId, status = ''] = id.split('|')
	return { workflowId, status }
}

// ─── Draggable card ──────────────────────────────────────────────────────────

function DraggableCard({
	wo,
	onOpen,
	parentOrderCode,
	workflows,
}: {
	wo: WorkOrder
	onOpen: () => void
	parentOrderCode?: string
	workflows: BoardWorkflow[]
}) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: wo.workOrderId, data: { wo } })
	// The 8px pointer-activation constraint means a plain click never starts a drag,
	// so a click on the wrapper opens the detail; a real drag suppresses it. The
	// pointer-travel guard is a belt-and-braces check against a click firing at the
	// tail of a drag gesture.
	const downAt = useRef({ x: 0, y: 0 })
	return (
		<div
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			// Compose with dnd-kit's own onPointerDown (from `listeners`) — spreading
			// then overriding it would swallow the event and the drag would never
			// start (grab cursor shows but nothing moves). Call dnd-kit's first.
			onPointerDown={(e) => {
				downAt.current = { x: e.clientX, y: e.clientY }
				listeners?.onPointerDown?.(e)
			}}
			onClick={(e) => {
				if (Math.hypot(e.clientX - downAt.current.x, e.clientY - downAt.current.y) < 8) onOpen()
			}}
			// The original stays in place as a dimmed ghost while its overlay copy drags.
			className={cn(
				'cursor-grab touch-none rounded-ds-card transition-shadow active:cursor-grabbing hover:shadow-ds-md',
				isDragging && 'opacity-40',
			)}
		>
			<WorkOrderCard wo={wo} parentOrderCode={parentOrderCode} workflows={workflows} />
		</div>
	)
}

// ─── Drop zone (a column body or an In-Review swimlane) ──────────────────────

function DropZone({ id, children, className }: { id: string; children: React.ReactNode; className?: string }) {
	const { setNodeRef, isOver } = useDroppable({ id })
	return (
		<div
			ref={setNodeRef}
			className={cn(
				'rounded-ds-md transition-colors',
				// Conventional drop cue: the zone lights up + rings when a card hovers.
				isOver && 'bg-ds-primary/5 outline outline-2 outline-dashed outline-ds-primary/40',
				className,
			)}
		>
			{children}
		</div>
	)
}

function EmptyLane() {
	return (
		<p className="rounded-ds-md border border-dashed border-ds-border py-6 text-center text-xs text-ds-text-subtle">
			Drop here
		</p>
	)
}

function Cards({
	orders,
	onOpen,
	codeById,
	workflows,
}: {
	orders: WorkOrder[]
	onOpen: (id: string) => void
	codeById: Map<string, string>
	workflows: BoardWorkflow[]
}) {
	return (
		<>
			{orders.map((wo) => (
				<DraggableCard
					key={wo.workOrderId}
					wo={wo}
					onOpen={() => onOpen(wo.workOrderId)}
					parentOrderCode={wo.parentWorkOrderId ? codeById.get(wo.parentWorkOrderId) : undefined}
					workflows={workflows}
				/>
			))}
		</>
	)
}

// ─── One configured column ───────────────────────────────────────────────────

function StatusColumn({
	workflowId,
	column,
	onOpen,
	codeById,
	workflows,
}: {
	workflowId: string
	column: BoardColumn
	onOpen: (id: string) => void
	codeById: Map<string, string>
	workflows: BoardWorkflow[]
}) {
	return (
		<CardShell as="section" className="flex min-h-0 min-w-72 flex-1 flex-col border-0 bg-ds-surface p-3">
			<div className="mb-3 flex shrink-0 items-center justify-between">
				<h3 className="font-mono text-xs uppercase tracking-wide text-ds-text-muted">{column.status.label}</h3>
				<Badge variant="secondary" size="sm">
					{column.orders.length}
				</Badge>
			</div>
			<DropZone
				id={dropId(workflowId, column.status.code)}
				className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-0.5"
			>
				{column.orders.length === 0 ? (
					<EmptyLane />
				) : (
					<Cards orders={column.orders} onOpen={onOpen} codeById={codeById} workflows={workflows} />
				)}
			</DropZone>
		</CardShell>
	)
}

// ─── One workflow's swimlane of columns ──────────────────────────────────────

function WorkflowGroup({
	group,
	onOpen,
	codeById,
	workflows,
}: {
	group: BoardWorkflowGroup
	onOpen: (id: string) => void
	codeById: Map<string, string>
	workflows: BoardWorkflow[]
}) {
	return (
		<section className="flex min-h-0 flex-auto flex-col">
			<h2 className="mb-2 shrink-0 text-sm font-semibold text-ds-text">{group.name}</h2>
			<div className="flex min-h-0 flex-1 gap-4 overflow-x-auto pb-4">
				{group.columns.map((column) => (
					<StatusColumn
						key={column.status.code}
						workflowId={group.workflowId}
						column={column}
						onOpen={onOpen}
						codeById={codeById}
						workflows={workflows}
					/>
				))}
			</div>
		</section>
	)
}

// ─── Orders no configuration can place ───────────────────────────────────────

function UnconfiguredGroup({
	orders,
	onOpen,
	codeById,
	workflows,
}: {
	orders: WorkOrder[]
	onOpen: (id: string) => void
	codeById: Map<string, string>
	workflows: BoardWorkflow[]
}) {
	return (
		<section aria-label="Unconfigured" className="flex min-h-0 flex-auto flex-col">
			<h2 className="mb-2 shrink-0 text-sm font-semibold text-ds-text">Unconfigured</h2>
			<p className="mb-3 shrink-0 text-xs text-ds-text-subtle">
				These orders have no workflow, or a status their workflow does not define. They are shown here rather than
				hidden — an administrator needs to repair their configuration.
			</p>
			{/* No drop zone: there is no column to drop into until the configuration is fixed. */}
			<div className="flex min-h-0 flex-col gap-3 overflow-y-auto pr-0.5 sm:max-w-72">
				<Cards orders={orders} onOpen={onOpen} codeById={codeById} workflows={workflows} />
			</div>
		</section>
	)
}

// ─── The board: workflows first, then their configured status columns ────────

export function BoardColumns({
	model,
	onOpen,
	workflows,
}: {
	model: BoardModel
	onOpen: (id: string) => void
	workflows: BoardWorkflow[]
}) {
	const codeById = new Map(
		[...model.groups.flatMap((g) => g.columns.flatMap((c) => c.orders)), ...model.unconfigured].map((w) => [
			w.workOrderId,
			w.orderCode,
		]),
	)
	return (
		<div className="flex min-h-0 flex-1 flex-col gap-6">
			{model.groups.map((group) => (
				<WorkflowGroup key={group.workflowId} group={group} onOpen={onOpen} codeById={codeById} workflows={workflows} />
			))}
			{model.unconfigured.length > 0 ? (
				<UnconfiguredGroup orders={model.unconfigured} onOpen={onOpen} codeById={codeById} workflows={workflows} />
			) : null}
		</div>
	)
}

/**
 * The overlay card that follows the cursor while dragging (rendered in
 * DragOverlay, in a portal above the scroll containers). Lifted + tilted with a
 * strong shadow — the conventional "picked up" cue.
 */
export function DragCard({ wo, workflows }: { wo: WorkOrder; workflows: BoardWorkflow[] }) {
	return (
		<div className="w-68 rotate-2 scale-[1.02] cursor-grabbing opacity-95 shadow-ds-xl">
			<WorkOrderCard wo={wo} workflows={workflows} />
		</div>
	)
}
